import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const setup = searchParams.get('mode') === 'setup';
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confirm) { setError('Passwords do not match'); return; }
    if (newPw.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/reset-password`, { token: resetToken, new_password: newPw });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!resetToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1B3A5C] to-[#0f2640] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-red-600 mb-4">Invalid reset link. Please request a new password reset.</p>
          <Link to="/portal/login" className="text-[#4A90A4] hover:underline">Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1B3A5C] to-[#0f2640] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-[#4A90A4]/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#4A90A4]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{setup ? 'Set Your Password' : 'Reset Your Password'}</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {success ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-[#1B3A5C] mb-2">{setup ? 'Your Account Is Ready' : 'Password Reset!'}</h2>
              <p className="text-gray-500 text-sm mb-6">You can now log in with your new password.</p>
              <Link to="/portal/login" className="inline-flex items-center px-6 py-3 bg-[#F5A623] text-white font-bold rounded-xl hover:brightness-110">
                Go to Login
              </Link>
            </div>
          ) : (
            <>
              {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}<Link to="/portal/login?mode=setup" className="block mt-2 underline">Request a new access link</Link></div>}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                  <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                    placeholder="Minimum 8 characters" required minLength={8}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                    data-testid="reset-new-password" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                    placeholder="Re-enter your password" required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                    data-testid="reset-confirm-password" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3.5 bg-[#4A90A4] text-white font-bold rounded-xl hover:brightness-110 transition-all disabled:opacity-50">
                  {loading ? 'Saving...' : setup ? 'Set Password' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
